import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { skillColor } from "@/lib/skills";
import {
  Check,
  Lock,
  Play,
  ClipboardCheck,
  RotateCcw,
  Languages,
  BookOpen,
  ChevronDown,
} from "lucide-react";
import { t } from "@/i18n";

/* Jauge de validation d'origine */
function ValidationGauge({ validations = 0, required = 3, needsRework }) {
  return (
    <div className="flex items-center gap-1 mt-0.5">
      {Array.from({ length: required }).map((_, i) => (
        <span
          key={i}
          className={`h-1 w-3 rounded-full shrink-0 ${
            i < validations
              ? needsRework
                ? "bg-ocre"
                : "bg-green-500"
              : "bg-muted-foreground/20"
          }`}
        />
      ))}
      <span className="text-[9px] text-muted-foreground ml-1 shrink-0">
        {validations}/{required}
      </span>
    </div>
  );
}

// Fonction pour retirer "Module X — " du titre
const cleanTitle = (title) => {
  if (!title) return "";
  return title.replace(/^Module\s+\d+\s*[-—]\s*/i, "");
};

export default function Roadmap() {
  const [sections, setSections] = useState([]);
  const [lessons, setLessons] = useState({});
  const [groups, setGroups] = useState([]);
  const [masteryTarget, setMasteryTarget] = useState(5);
  const [required, setRequired] = useState(3);
  const [tab, setTab] = useState("lessons");
  const [openSecs, setOpenSecs] = useState({});
  const nav = useNavigate();

  // Pour l'accordéon du vocabulaire
  const [expandedVocabCategory, setExpandedVocabCategory] = useState(null);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);

  useEffect(() => {
    api
      .get("/content/roadmap")
      .then((r) => setSections(r.data))
      .catch(() => {});
    api
      .get("/progression/lessons")
      .then((r) => {
        setLessons(r.data.lessons || {});
        setRequired(r.data.required || 3);
      })
      .catch(() => {});
    api
      .get("/progression/vocab-groups")
      .then((r) => {
        setGroups(r.data.groups || []);
        setMasteryTarget(r.data.mastery_target || 5);
      })
      .catch(() => {});
  }, []);

  // Ouvre automatiquement la catégorie mère qui contient le groupe en cours
  useEffect(() => {
    if (groups.length > 0 && !hasAutoExpanded) {
      const currentGroup = groups.find((g) => g.status === "current");
      if (currentGroup) {
        setExpandedVocabCategory(currentGroup.category || "Général");
      }
      setHasAutoExpanded(true);
    }
  }, [groups, hasAutoExpanded]);

  const toggleSection = (id) => {
    setOpenSecs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Grouper les mots par catégorie mère
  const groupedCategories = useMemo(() => {
    const map = {};
    groups.forEach((g) => {
      const catName = g.category || "Général";
      if (!map[catName]) map[catName] = [];
      map[catName].push(g);
    });
    return map;
  }, [groups]);

  // Filtrer les examens et modules futurs
  const validSections = sections.filter((sec) => !sec.exam && !sec.future);

  return (
    <div className="w-full max-w-6xl mx-auto px-3 lg:px-1 py-1 md:py-8 overflow-x-hidden">
      {/* En-tête */}
      <div className="mb-4 w-full">
        <h1 className="font-head text-2xl lg:text-3xl font-bold mb-1">
          {t("roadmap.ton_parcours")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("roadmap.chaque_lecon_se_valide")} {required}{" "}
          {t("roadmap.fois_une_par_jour_a_80")}
        </p>
      </div>

      {/* Bouton Fiches (Mobile) */}

      {/* Onglets (Mobile) */}
      <div className="flex gap-2 mb-4 lg:hidden w-full">
        <button
          onClick={() => setTab("lessons")}
          className={`flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 transition ${
            tab === "lessons"
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
          }`}
        >
          <BookOpen size={15} /> {t("admin.lecons")}
        </button>
        <button
          onClick={() => setTab("vocab")}
          className={`flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 transition ${
            tab === "vocab"
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
          }`}
        >
          <Languages size={15} /> {t("roadmap.vocabulaire")}
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start w-full">
        {/* ==========================================
            COLONNE GAUCHE : LEÇONS
        ========================================== */}
        <div
          className={`w-full min-w-0 ${tab === "lessons" ? "block" : "hidden lg:block"}`}
        >
          <div className="space-y-3 w-full">
            {validSections.map((sec, si) => {
              const color = skillColor(sec.color);
              const isOpen = openSecs[si] || false;
              const displayTitle = cleanTitle(sec.title);

              return (
                <div
                  key={si}
                  className="rounded-xl border bg-card p-3 lg:p-4 transition-all w-full min-w-0"
                  style={{ borderLeft: `4px solid ${color}` }}
                >
                  {/* Titre cliquable de l'accordéon */}
                  <div
                    className="flex items-center justify-between gap-3 cursor-pointer select-none w-full min-w-0"
                    onClick={() => toggleSection(si)}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span
                        className="text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full text-white shrink-0"
                        style={{ background: color }}
                      >
                        {sec.level}
                      </span>
                      <h2 className="font-head text-sm md:text-base font-bold truncate">
                        {displayTitle}
                      </h2>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs font-semibold" style={{ color }}>
                        {sec.progress}%
                      </span>
                      <ChevronDown
                        size={18}
                        className={`text-muted-foreground transition-transform duration-200 shrink-0 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </div>

                  {/* Contenu affiché uniquement quand le bloc est déplié */}
                  {isOpen && (
                    <div className="mt-2 pt-2 animate-in fade-in slide-in-from-top-2 duration-200 w-full min-w-0">
                      <p className="text-xs text-muted-foreground mb-3 break-words">
                        {sec.desc}
                      </p>

                      {sec.nodes.length > 0 && (
                        <>
                          <div className="h-1.5 w-full rounded-full bg-secondary mb-3 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${sec.progress}%`,
                                background: color,
                              }}
                            />
                          </div>

                          <div className="grid gap-2 w-full">
                            {sec.nodes.map((n) => {
                              const st = lessons[n.id] || {};
                              const done =
                                n.status === "completed" || st.acquired;
                              const nlocked =
                                n.status === "locked" && !st.acquired;
                              const ncolor = skillColor(n.skill);
                              const isDb = n.type === "db-lesson";
                              const rework = !!st.needs_rework;

                              return (
                                <button
                                  key={n.id}
                                  disabled={nlocked}
                                  onClick={() =>
                                    nav(
                                      isDb
                                        ? "/daily-plan"
                                        : `/learn/lesson/${n.id}`,
                                    )
                                  }
                                  className={`flex items-center gap-3 w-full min-w-0 rounded-lg border p-2 text-left transition ${
                                    nlocked
                                      ? "opacity-50"
                                      : "hover:border-primary bg-card hover:bg-muted/30"
                                  } ${rework ? "border-ocre/60" : ""}`}
                                >
                                  <div
                                    className="w-8 h-8 rounded-md flex items-center justify-center text-white shrink-0 shadow-sm"
                                    style={{
                                      background: rework
                                        ? "#D97706"
                                        : done
                                          ? "#16A34A"
                                          : nlocked
                                            ? "hsl(var(--muted-foreground)/0.4)"
                                            : ncolor,
                                    }}
                                  >
                                    {rework ? (
                                      <RotateCcw size={14} />
                                    ) : done ? (
                                      <Check size={16} />
                                    ) : nlocked ? (
                                      <Lock size={14} />
                                    ) : (
                                      <Play size={14} />
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs md:text-sm font-medium leading-tight truncate">
                                      {n.label}
                                      {isDb && (
                                        <span className="ml-1 text-[10px] font-normal text-muted-foreground shrink-0">
                                          {t("roadmap.db")}
                                        </span>
                                      )}
                                    </div>
                                    {!isDb && !nlocked && (
                                      <ValidationGauge
                                        validations={st.validations || 0}
                                        required={st.required || required}
                                        needsRework={rework}
                                      />
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          {sec.test && (
                            <div
                              className="mt-3 flex items-center justify-between gap-3 w-full rounded-xl border-2 border-dashed p-3"
                              style={{
                                borderColor: sec.test.passed
                                  ? "#16A34A"
                                  : "hsl(var(--border))",
                              }}
                            >
                              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                <ClipboardCheck
                                  size={18}
                                  style={{
                                    color: sec.test.passed ? "#16A34A" : color,
                                  }}
                                  className="shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-semibold truncate">
                                    {t("moduletest.test_du_module_2")}{" "}
                                    {sec.test.passed && (
                                      <span style={{ color: "#16A34A" }}>
                                        {t("roadmap.valide")}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {sec.test.available
                                      ? `${sec.test.n_questions} questions · 80% pour valider`
                                      : t(
                                          "roadmap.termine_toutes_les_lecons_pour_debloquer",
                                        )}
                                  </div>
                                </div>
                              </div>
                              <button
                                disabled={!sec.test.available}
                                onClick={() => nav(`/module/${sec.id}/test`)}
                                className={`px-4 py-2 rounded-lg text-xs font-semibold shrink-0 transition ${
                                  !sec.test.available
                                    ? "border bg-muted/50 text-muted-foreground cursor-not-allowed"
                                    : sec.test.passed
                                      ? "border hover:bg-secondary"
                                      : "bg-primary text-primary-foreground hover:opacity-90"
                                }`}
                              >
                                {sec.test.passed
                                  ? t("roadmap.refaire")
                                  : t("roadmap.passer")}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ==========================================
            COLONNE DROITE : VOCABULAIRE EN ACCORDÉONS
        ========================================== */}
        <div
          className={`w-full min-w-0 ${tab === "vocab" ? "block" : "hidden lg:block"}`}
        >
          <div className="rounded-xl border bg-card p-4 sticky top-24 w-full">
            <div className="flex items-center gap-2 mb-2 w-full">
              <Languages size={18} className="text-sky-500 shrink-0" />
              <h2 className="font-head text-base font-bold truncate">
                {t("roadmap.vocabulaire")}
              </h2>
              <span className="ml-auto text-xs font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-full shrink-0">
                {groups.filter((g) => g.validated).length}/{groups.length}
              </span>
            </div>

            <p className="text-xs text-muted-foreground mb-4 break-words">
              {t("roadmap.apprends")} {groups[0]?.gate_pct ?? 90}
              {t("roadmap.des_mots_pour_valider_un_groupe")} {masteryTarget}{" "}
              {t("roadmap.reussites")}
            </p>

            {groups.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-4">
                {t("roadmap.aucun_groupe_pour_l_instant")}
              </div>
            ) : (
              <div className="space-y-3 w-full">
                {Object.entries(groupedCategories).map(
                  ([catName, catGroups]) => {
                    const isCatExpanded = expandedVocabCategory === catName;

                    // Calculs des statistiques de la catégorie mère
                    let catMastered = 0;
                    let catTotal = 0;
                    let allValidated = true;

                    catGroups.forEach((g) => {
                      catMastered += g.mastered;
                      catTotal += g.total;
                      if (!g.validated) allValidated = false;
                    });

                    const catPct =
                      catTotal > 0
                        ? Math.round((catMastered / catTotal) * 100)
                        : 0;

                    return (
                      <div
                        key={catName}
                        className="rounded-2xl border-2 overflow-hidden bg-card transition-all"
                      >
                        {/* En-tête Catégorie Mère */}
                        <div className="flex items-stretch border-b last:border-b-0 bg-secondary/5 hover:bg-secondary/10 transition">
                          <button
                            onClick={() =>
                              setExpandedVocabCategory(
                                isCatExpanded ? null : catName,
                              )
                            }
                            className="flex-1 p-3 text-left flex justify-between items-center"
                          >
                            <div className="min-w-0 flex-1 pr-2">
                              <span className="font-bold text-sm truncate block">
                                {catName}
                              </span>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="h-1.5 w-16 rounded-full bg-secondary overflow-hidden shrink-0">
                                  <div
                                    className="h-full rounded-full transition-all"
                                    style={{
                                      width: `${catPct}%`,
                                      backgroundColor: allValidated
                                        ? "#16A34A"
                                        : "#0EA5E9",
                                    }}
                                  />
                                </div>
                                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                  {catMastered}/{catTotal} mots
                                </span>
                              </div>
                            </div>
                            <span className="text-xs font-medium text-muted-foreground bg-background px-2 py-1 rounded-md border shrink-0">
                              {catGroups.length}{" "}
                              <span className="hidden md:inline">
                                {t("roadmap.sous_cat")}
                              </span>
                              <span className="md:hidden">
                                {t("roadmap.sc")}
                              </span>
                            </span>
                          </button>
                          <button
                            onClick={() =>
                              setExpandedVocabCategory(
                                isCatExpanded ? null : catName,
                              )
                            }
                            className="shrink-0 w-10 grid place-items-center border-l hover:bg-secondary/20 transition"
                          >
                            <ChevronDown
                              size={16}
                              className={`transition-transform ${isCatExpanded ? "rotate-180" : ""}`}
                            />
                          </button>
                        </div>

                        {/* Sous-catégories (Les carrés) */}
                        {isCatExpanded && (
                          <div className="p-3 grid grid-cols-2 gap-3 bg-secondary/5 border-t">
                            {catGroups.map((g) => (
                              <button
                                key={g.id}
                                onClick={() =>
                                  nav(
                                    `/vocab-quiz?group=${encodeURIComponent(g.id)}`,
                                  )
                                }
                                className={`aspect-square w-full flex flex-col items-center justify-center text-center p-2 md:p-3 rounded-2xl border transition-all hover:scale-[1.02] hover:shadow-sm min-w-0 bg-card ${
                                  g.validated
                                    ? "border-green-500/30 hover:border-green-500/60"
                                    : g.status === "current"
                                      ? "border-primary hover:border-primary"
                                      : "hover:border-muted-foreground/40"
                                }`}
                              >
                                {/* Nom de la sous-catégorie */}
                                <div className="font-semibold text-[11px] md:text-sm mb-1 leading-tight line-clamp-2 w-full px-1 break-words">
                                  {g.sous_categorie || g.title}
                                </div>

                                {/* Mots maîtrisés / Total */}
                                <div className="text-[10px] md:text-xs font-medium text-muted-foreground mb-2 shrink-0">
                                  {g.mastered} / {g.total}
                                </div>

                                {/* Barre de progression ou statut */}
                                <div className="w-3/4 h-1.5 rounded-full bg-secondary overflow-hidden mb-1.5 shrink-0">
                                  <div
                                    className="h-full rounded-full transition-all"
                                    style={{
                                      width: `${g.pct}%`,
                                      background: g.validated
                                        ? "#16A34A"
                                        : "#0EA5E9",
                                    }}
                                  />
                                </div>

                                {g.validated ? (
                                  <span className="text-[9px] font-bold text-green-600 shrink-0">
                                    {t("roadmap.valide_2")}
                                  </span>
                                ) : g.status === "current" ? (
                                  <span className="text-[9px] font-bold text-primary shrink-0">
                                    {t("roadmap.en_cours")}
                                  </span>
                                ) : (
                                  <span className="text-[9px] font-bold text-transparent select-none shrink-0">
                                    -
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
